import { List } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthPermissions } from 'skipify-types';

import { CustomNavLink } from './CustomNavLink';
import { CustomNavGroup } from './CustomNavGroup';
import { ConfigurationPath, configurationPaths } from '../../util/constants';
import { getAppBarHeight } from '../../util/helpers';
import { useCanManagePortalUsers, useIsSkipifyAdmin } from '../../hooks';
import { useMerchantAwareNavigate } from '../../hooks/useMerchantAwareNavigate';
import { useMerchant, isMerchantIdParentPickerRow } from '../../contexts/MerchantContext';

export type NavLinkMapping = {
  label: string;
  path: ConfigurationPath;
  isAdminOnly?: boolean;
  requiresCreateMerchants?: boolean;
  requiresManagePortalUsers?: boolean;
};

type Props = {
  onCloseDrawer?: () => void;
};

const configTabSidebar: NavLinkMapping[] = [
  {
    label: 'Home',
    path: configurationPaths.SETTINGS,
  },
  {
    label: 'Payments & Processing',
    path: configurationPaths.PAYMENTS_AND_PROCESSING,
  },
  {
    label: 'Merchant domains',
    path: configurationPaths.MERCHANT_DOMAINS,
  },
  {
    label: 'API Keys',
    path: configurationPaths.API_KEYS,
  },
  {
    label: 'Branding',
    path: configurationPaths.BRANDING,
  },
  {
    label: 'Webhooks',
    path: configurationPaths.MERCHANT_WEBHOOKS,
  },
  {
    label: 'Create Child Merchant',
    path: configurationPaths.CREATE_CHILD_MERCHANT,
    requiresCreateMerchants: true,
  },
  {
    label: 'Paylinks',
    path: configurationPaths.PAYLINKS,
  },
  {
    label: 'Agentic Checkout',
    path: configurationPaths.AGENTIC_CHECKOUT,
    isAdminOnly: true,
  },
  {
    label: 'Audit Logs',
    path: configurationPaths.AUDIT_LOGS,
    isAdminOnly: true,
  },
  {
    label: 'Users',
    path: configurationPaths.MERCHANT_USERS,
    requiresManagePortalUsers: true,
  },
];

const portalUserManagementSidebar: NavLinkMapping[] = [
  {
    label: 'Manage users',
    path: configurationPaths.PORTAL_USERS,
  },
  {
    label: 'Roles & permissions',
    path: configurationPaths.PORTAL_USERS_ROLES,
  },
  {
    label: 'Audit logs',
    path: configurationPaths.PORTAL_USERS_AUDIT_LOGS,
  },
];

const adminSidebar: NavLinkMapping[] = [
  {
    label: 'Create merchant',
    path: configurationPaths.ADMIN_CREATE_MERCHANT,
  },
  ...portalUserManagementSidebar,
  {
    label: 'Shopify provisioning',
    path: configurationPaths.ADMIN_SHOPIFY_PROVISIONING,
  },
];

const checkoutCustomizationsGroup: NavLinkMapping[] = [
  {
    label: 'Tipping',
    path: configurationPaths.TIPPING,
  },
  {
    label: 'Fees & Discounts',
    path: configurationPaths.FEES,
  },
  {
    label: 'Cart Edits',
    path: configurationPaths.CART_EDITS,
  },
  {
    label: 'Shopper Consent',
    path: configurationPaths.SHOPPER_CONSENT,
  },
  {
    label: 'Fulfillment',
    path: configurationPaths.FULFILLMENT,
  },
];

const partnersGroup: NavLinkMapping[] = [
  {
    label: 'Flex Pay',
    path: configurationPaths.FLEX_PAY,
  },
  {
    label: 'Click to Pay',
    path: configurationPaths.CLICK_TO_PAY,
    isAdminOnly: true,
  },
];

/**
 * Navigation menu for drawer
 * @param {Object} props Component properties
 * @returns {JSX.Element} Nav list
 */
export const NavContent = ({ onCloseDrawer }: Props): JSX.Element => {
  const isSkipifyAdmin = useIsSkipifyAdmin();
  const canManagePortalUsers = useCanManagePortalUsers();
  const {
    isChildMerchantOnlyUser,
    canAccess,
    hasMerchantAccess,
    requiresParentSelection,
    pendingChildMerchantLandingAfterParentPick,
    partnerMerchantId,
    merchantId,
    parentMerchants,
  } = useMerchant();
  const location = useLocation();
  const navigate = useMerchantAwareNavigate();

  const merchantIdIsParentPickerRow = isMerchantIdParentPickerRow(merchantId, parentMerchants);

  const useMinimalChildOnlySidebar = useMemo(
    () =>
      isChildMerchantOnlyUser &&
      (requiresParentSelection ||
        pendingChildMerchantLandingAfterParentPick ||
        merchantIdIsParentPickerRow ||
        partnerMerchantId == null ||
        partnerMerchantId === ''),
    [
      isChildMerchantOnlyUser,
      requiresParentSelection,
      pendingChildMerchantLandingAfterParentPick,
      merchantIdIsParentPickerRow,
      partnerMerchantId,
    ],
  );

  const partnersVisibleLinks = useMemo(
    () => partnersGroup.filter((link) => !link.isAdminOnly || isSkipifyAdmin),
    [isSkipifyAdmin],
  );

  const [checkoutCustomizationsOpen, setCheckoutCustomizationsOpen] = useState(
    !!checkoutCustomizationsGroup.find((member) => member.path === location.pathname),
  );
  const [partnersOpen, setPartnersOpen] = useState(
    !!partnersVisibleLinks.find((member) => member.path === location.pathname),
  );

  const navListSx = {
    paddingTop: '20px !important',
    backgroundColor: 'background.dark',
    height: {
      xs: `100vh`,
      sm: `100vh`,
      md: `calc(100vh - ${getAppBarHeight()}px)`,
    },
    overflowY: 'auto',
  } as const;

  if (!hasMerchantAccess()) {
    return <List sx={navListSx} />;
  }

  const isAdminSection = location.pathname.startsWith(configurationPaths.ADMIN);
  const isPortalUserManagementSection =
    location.pathname.startsWith(configurationPaths.PORTAL_USERS) && canManagePortalUsers;

  // Skipify admins use the full admin sidebar on portal-user routes too (Create merchant, Shopify, etc.).
  const elevatedSidebar =
    isAdminSection || (isPortalUserManagementSection && isSkipifyAdmin)
      ? adminSidebar
      : isPortalUserManagementSection
        ? portalUserManagementSidebar
        : null;

  if (elevatedSidebar) {
    return (
      <List sx={navListSx}>
        {elevatedSidebar.map((navItem) => (
          <CustomNavLink
            label={navItem.label}
            path={navItem.path}
            key={navItem.label}
            onClick={onCloseDrawer}
            selected={
              navItem.path === configurationPaths.PORTAL_USERS
                ? location.pathname === navItem.path
                : location.pathname.startsWith(navItem.path)
            }
          />
        ))}
      </List>
    );
  }

  if (useMinimalChildOnlySidebar) {
    const canCreateChildMerchants = canAccess(AuthPermissions.CREATE_MERCHANTS);
    return (
      <List sx={navListSx}>
        <CustomNavLink
          label="Home"
          path={configurationPaths.SETTINGS}
          key="Home"
          onClick={onCloseDrawer}
          selected={location.pathname === configurationPaths.SETTINGS}
        />
        {canCreateChildMerchants && !requiresParentSelection ? (
          <CustomNavLink
            label="Create Child Merchant"
            path={configurationPaths.CREATE_CHILD_MERCHANT}
            key="Create Child Merchant"
            onClick={onCloseDrawer}
            selected={location.pathname === configurationPaths.CREATE_CHILD_MERCHANT}
          />
        ) : null}
      </List>
    );
  }

  return (
    <List sx={navListSx}>
      {configTabSidebar
        .filter(
          (navItem) =>
            !!navItem &&
            (!navItem.isAdminOnly || isSkipifyAdmin) &&
            (!navItem.requiresManagePortalUsers || canManagePortalUsers) &&
            (!navItem.requiresCreateMerchants || canAccess(AuthPermissions.CREATE_MERCHANTS)) &&
            !(navItem.path === configurationPaths.CREATE_CHILD_MERCHANT && requiresParentSelection),
        )
        .map((navItem) => (
          <CustomNavLink
            label={navItem.label}
            path={navItem.path}
            key={navItem.label}
            onClick={onCloseDrawer}
            selected={location.pathname === navItem.path}
          />
        ))}
      <CustomNavGroup
        key="Checkout Customizations"
        groupName="Checkout Customizations"
        onClick={() => {
          if (!checkoutCustomizationsOpen) {
            navigate(checkoutCustomizationsGroup[0].path);
          }
          if (
            checkoutCustomizationsOpen &&
            !!checkoutCustomizationsGroup.find((link) => link.path === location.pathname)
          ) {
            navigate(configurationPaths.SETTINGS);
          }
          setCheckoutCustomizationsOpen((prev) => !prev);
          onCloseDrawer?.();
        }}
        isOpen={checkoutCustomizationsOpen}
        links={checkoutCustomizationsGroup}
        onCloseDrawer={onCloseDrawer}
        nestLevel={1}
      />
      {partnersVisibleLinks.length > 0 && (
        <CustomNavGroup
          key="Partners"
          groupName="Partners"
          onClick={() => {
            if (!partnersOpen) {
              navigate(partnersVisibleLinks[0].path);
            }
            if (
              partnersOpen &&
              !!partnersVisibleLinks.find((link) => link.path === location.pathname)
            ) {
              navigate(configurationPaths.SETTINGS);
            }
            setPartnersOpen((prev) => !prev);
            onCloseDrawer?.();
          }}
          isOpen={partnersOpen}
          links={partnersVisibleLinks}
          onCloseDrawer={onCloseDrawer}
          nestLevel={1}
        />
      )}
    </List>
  );
};
